import ContextFlagSection from "./ContextFlagSection";
import { cardBorder } from "../styles/design-tokens";

// ── Type color palette ─────────────────────────────────────────────────────────
const TYPE_COLORS = {
  intervals: { main:'#f97316', rgb:'251,115,22',  light:'#fb923c', grad:'linear-gradient(135deg,#f97316,#ea580c)' },
  tempo:     { main:'#8b5cf6', rgb:'139,92,246',  light:'#a78bfa', grad:'linear-gradient(135deg,#7c3aed,#4f46e5)' },
  easy:      { main:'#38bdf8', rgb:'56,189,248',  light:'#7dd3fc', grad:'linear-gradient(135deg,#38bdf8,#0ea5e9)' },
  long:      { main:'#38bdf8', rgb:'56,189,248',  light:'#7dd3fc', grad:'linear-gradient(135deg,#38bdf8,#0ea5e9)' },
  hills:     { main:'#34d399', rgb:'52,211,153',  light:'#6ee7b7', grad:'linear-gradient(135deg,#34d399,#059669)' },
  race:      { main:'#f87171', rgb:'248,113,113', light:'#fca5a5', grad:'linear-gradient(135deg,#ef4444,#dc2626)' },
  unplanned: { main:'#64748b', rgb:'100,116,139', light:'#94a3b8', grad:'linear-gradient(135deg,#475569,#334155)' },
};

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  cardBg:     'linear-gradient(160deg,#1a1535,#141029)',
  border:     'rgba(139,92,246,0.28)',
  divider:    'rgba(139,92,246,0.15)',
  accent:     '#8b5cf6',
  textPrimary:'#e8e6f5',
  textMuted:  '#b8b2d8',
  textFaint:  '#8b85b0',
  good:       '#5dcaa5',
};

// ── 2.1 Trend insight block ───────────────────────────────────────────────────
function TrendBlock({ trendData, tc }) {
  const { verdict, card_mode, insight_text, today_workout, payload } = trendData;
  const isInsight = card_mode === 'insight';
  const hrDelta   = payload?.hr_delta;                        // absolute value for display
  const hrSigned  = payload?.comparison?.hr_delta;            // signed: + = today HR higher
  const hrIsHigher = hrSigned != null ? hrSigned > 0 : (verdict === 'declining');
  const compCount = payload?.comparison?.count || 0;
  const isDecline = verdict === 'declining';
  const isImprove = verdict === 'improving';
  const vColor    = isDecline ? '#fbbf24' : isImprove ? '#5dcaa5' : C.textMuted;
  const vLabel    = { improving:'שיפור', declining:'ירידה', stable:'יציב', first:'ראשוני', single:'ראשוני', hint:'ראשוני' }[verdict] || verdict;
  const dist      = today_workout?.distance;
  // Intervals/tempo: distance is the FULL run (total), but pace/HR are the QUALITY SEGMENT.
  // qKind ('חזרות'/'טמפו') tags them so "8.9K · 4:20" is never shown unlabelled (which would
  // read as "ran 8.9K at 4:20" — false). qDist = the segment volume for the "of which" note.
  const qKind     = today_workout?.quality_kind;
  const qDist     = today_workout?.quality_distance;
  // Few data points → a FACTUAL observation, not a trend. Neutral blue badge so it
  // never reads as a proven "שיפור"/"ירידה" (which need ≥3 comparable workouts).
  const isObservation = ['first', 'single', 'hint'].includes(verdict);
  const obsLabel = verdict === 'first' ? 'נקודת ייחוס' : 'תצפית מוקדמת';

  // Plan-compliance verdicts (2026-08-23): the card judges plan adherence only — never
  // fitness. Direction of a deviation is deliberately not judged (too fast ≠ good).
  const COMPLIANCE = {
    on_plan:   { icon: '✅', label: 'בהתאם לתכנית',                 color: '#5dcaa5', rgb: '93,202,165' },
    deviation: { icon: '⚠️', label: 'סטייה מהתכנון — שווה לבדוק',   color: '#fbbf24', rgb: '251,191,36' },
    easy_done: { icon: '💤', label: 'קל בכוונה — בוצע כמתוכנן',     color: '#38bdf8', rgb: '56,189,248' },
    completed: { icon: '📝', label: 'הושלם ונרשם',                  color: '#94a3b8', rgb: '148,163,184' },
  };
  const comp = COMPLIANCE[verdict];

  return (
    <>
      {/* Type-colored header band */}
      <div style={{
        background: `linear-gradient(135deg,rgba(${tc.rgb},0.28),rgba(${tc.rgb},0.05))`,
        padding: '13px 16px 11px',
        display: 'flex', alignItems: 'center', gap: 9,
        borderBottom: `1px solid rgba(${tc.rgb},0.35)`,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
          background: tc.grad,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
        }}>💡</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>תובנות המאמן</div>
          {today_workout?.label && (
            <div style={{ fontSize: 11, color: tc.light, fontWeight: 500 }}>
              {today_workout.label}
              {today_workout.date ? (
                <span style={{ color: C.textFaint, fontWeight: 400 }}>
                  {' · '}{today_workout.date.slice(5,10).replace('-','/')}
                </span>
              ) : null}
              {dist > 0 && !isObservation && !comp ? <span style={{ color: C.textFaint, fontWeight: 400 }}> · ממוצע אחרון</span> : null}
            </div>
          )}
        </div>
        {dist > 0 && (
          <span style={{
            fontSize: 11, color: '#0a0f16', background: tc.main,
            padding: '3px 10px', borderRadius: 6, fontWeight: 600, flexShrink: 0,
          }}>{dist}K</span>
        )}
      </div>

      {/* Insight text */}
      <div style={{ padding: '13px 16px 11px', fontSize: 15, lineHeight: 1.6, color: C.textPrimary, direction: 'rtl' }}>
        {insight_text}
      </div>

      {/* Plan-compliance badge — the card's one-glance answer: ✅ / ⚠️ / 💤 */}
      {comp && (
        <div style={{ margin: '0 16px 12px', direction: 'rtl' }}>
          <span style={{
            fontSize: 12, fontWeight: 700, color: comp.color,
            background: `rgba(${comp.rgb},0.12)`,
            border: `1px solid rgba(${comp.rgb},0.32)`,
            padding: '4px 11px', borderRadius: 7,
          }}>{comp.icon} {comp.label}</span>
        </div>
      )}

      {/* Observation badge (few data points) — neutral blue, NOT a trend claim */}
      {isObservation && (
        <div style={{ margin: '0 16px 12px', direction: 'rtl' }}>
          <span style={{
            fontSize: 11, fontWeight: 600, color: '#38bdf8',
            background: 'rgba(56,189,248,0.12)',
            border: '1px solid rgba(56,189,248,0.28)',
            padding: '3px 10px', borderRadius: 6,
          }}>🔍 {obsLabel}</span>
        </div>
      )}

      {/* Verdict block */}
      {isInsight && hrDelta != null && (
        <div style={{
          margin: '0 16px 12px',
          background: `rgba(${isDecline ? '251,191,36' : '93,202,165'},0.07)`,
          borderInlineStart: `3px solid ${vColor}`,
          padding: '9px 12px',
        }}>
          <div style={{ fontSize: 11, color: vColor, marginBottom: 2 }}>{vLabel}</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: C.textMuted }}>
            {/* Explanation must MATCH the verdict — never say "fatigue" on an improving verdict.
                A higher HR at a much faster pace = efficiency, not fatigue. */}
            {/* 'declining' is an EFFICIENCY verdict (beats per km) — a slower pace at the SAME
                HR lands here with no HR rise at all. Saying "דופק גבוה" on a run whose HR was
                1 beat LOWER contradicted the card's own sentence (user 2026-08-09). Only claim
                a rise when the SIGNED delta actually shows one. */}
            {isDecline
              ? (hrSigned != null && hrSigned > 0
                  ? `דופק גבוה ב-${hrDelta} פעימות מהממוצע — שים לב לעייפות מצטברת.`
                  : hrSigned != null && hrSigned < 0
                    ? `הדופק לא עלה — נמוך ב-${hrDelta} פעימות מהממוצע — אך הקצב היה איטי יותר, כלומר יותר פעימות לכל ק"מ.`
                    : `הדופק דומה לממוצע, אך הקצב היה איטי יותר — יותר פעימות לכל ק"מ.`)
              : hrIsHigher
                ? `דופק גבוה ב-${hrDelta} פעימות — אך הקצב היה מהיר יותר, היעילות השתפרה.`
                : `דופק נמוך ב-${hrDelta} פעימות מהממוצע — הביצועים משתפרים.`}
          </div>
        </div>
      )}

      {/* Rep trend (negative/positive split within an interval set) — the intra-set story the
          average-based verdict misses. Only present when genuine per-rep paces existed. */}
      {trendData?.rep_trend?.text && (() => {
        const neg = trendData.rep_trend.direction === 'negative_split';
        const tColor = neg ? '#5dcaa5' : '#fbbf24';
        return (
          <div style={{
            margin: '0 16px 12px',
            background: `rgba(${neg ? '93,202,165' : '251,191,36'},0.08)`,
            borderInlineStart: `3px solid ${tColor}`,
            padding: '9px 12px', direction: 'rtl',
          }}>
            <div style={{ fontSize: 11, color: tColor, marginBottom: 2 }}>
              {neg ? '📈 מגמת חזרות' : '📉 מגמת חזרות'}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: C.textMuted }}>
              {trendData.rep_trend.text}
            </div>
          </div>
        );
      })()}

      {/* Progression (continuous progressive run) — the run's thirds got faster. The central
          insight of a progressive long run, which the average-based verdict flattens. */}
      {trendData?.progression?.text && (
        <div style={{
          margin: '0 16px 12px',
          background: 'rgba(93,202,165,0.08)',
          borderInlineStart: '3px solid #5dcaa5',
          padding: '9px 12px', direction: 'rtl',
        }}>
          <div style={{ fontSize: 11, color: '#5dcaa5', marginBottom: 2 }}>📈 מגמת התקדמות</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: C.textMuted }}>
            {trendData.progression.text}
          </div>
        </div>
      )}

      {/* HR drift (aerobic decoupling) — a steady long run whose HR climbed within the run. The
          whole-run average HR hides it; ≥10% is amber (durability limit), else teal (healthy). */}
      {trendData?.hr_drift?.text && (() => {
        const high = (trendData.hr_drift.drift_pct || 0) >= 10;
        const dColor = high ? '#fbbf24' : '#5dcaa5';
        return (
          <div style={{
            margin: '0 16px 12px',
            background: `rgba(${high ? '251,191,36' : '93,202,165'},0.08)`,
            borderInlineStart: `3px solid ${dColor}`,
            padding: '9px 12px', direction: 'rtl',
          }}>
            <div style={{ fontSize: 11, color: dColor, marginBottom: 2 }}>💓 זחילת דופק</div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: C.textMuted }}>
              {trendData.hr_drift.text}
            </div>
          </div>
        );
      })()}

      {/* Route elevation — a hilly route raises HR / slows pace; the note reframes those numbers as
          terrain, not a fitness dip. Density-gated on the backend, so it appears only when it matters. */}
      {trendData?.elevation?.text && (() => {
        const steep = trendData.elevation.tier === 'significant';
        const eColor = steep ? '#d6935c' : '#c2a36b';
        return (
          <div style={{
            margin: '0 16px 12px',
            background: `rgba(${steep ? '214,147,92' : '194,163,107'},0.08)`,
            borderInlineStart: `3px solid ${eColor}`,
            padding: '9px 12px', direction: 'rtl',
          }}>
            <div style={{ fontSize: 11, color: eColor, marginBottom: 2 }}>⛰️ מסלול עם עליות</div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: C.textMuted }}>
              {trendData.elevation.text}
            </div>
          </div>
        );
      })()}

      {/* Stats chips */}
      <div style={{ display: 'flex', gap: 6, padding: '0 16px 12px', flexWrap: 'wrap' }}>
        {today_workout?.pace && (
          <span style={{
            fontSize: 11, background: `rgba(${tc.rgb},0.15)`, color: tc.light,
            padding: '3px 9px', borderRadius: 6,
          }}>{qKind ? `קצב ${qKind} ${today_workout.pace}` : `${dist}K · ${today_workout.pace}`}</span>
        )}
        {today_workout?.hr && (
          <span style={{
            fontSize: 11, background: 'rgba(251,191,36,0.1)', color: '#fbbf24',
            padding: '3px 9px', borderRadius: 6,
          }}>{qKind ? `דופק ${qKind} ${today_workout.hr}` : `דופק ${today_workout.hr}`}</span>
        )}
        {qKind && qDist > 0 && (
          <span style={{
            fontSize: 11, background: 'rgba(148,163,184,0.12)', color: C.textMuted,
            padding: '3px 9px', borderRadius: 6,
          }}>מזה {qDist}K {qKind}</span>
        )}
        {compCount > 0 && (
          <span style={{ fontSize: 10, color: C.textFaint, padding: '3px 4px' }}>
            vs {compCount} אימונים קודמים
          </span>
        )}
      </div>
    </>
  );
}

// ── Unplanned workout card (no comparative analysis) ─────────────────────────
function UnplannedBlock({ trendData, tc }) {
  const { insight_text, today_workout } = trendData;
  const dist = today_workout?.distance;
  return (
    <>
      <div style={{
        background: `linear-gradient(135deg,rgba(${tc.rgb},0.28),rgba(${tc.rgb},0.05))`,
        padding: '13px 16px 11px',
        display: 'flex', alignItems: 'center', gap: 9,
        borderBottom: `1px solid rgba(${tc.rgb},0.35)`,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
          background: tc.grad,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
        }}>👏</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>אימון לא מתוכנן</div>
          {today_workout?.date && (
            <div style={{ fontSize: 11, color: tc.light, fontWeight: 500 }}>
              {today_workout.date.slice(5,10).replace('-','/')}
              {dist > 0 ? <span style={{ color: C.textFaint, fontWeight: 400 }}> · {dist} ק"מ</span> : null}
            </div>
          )}
        </div>
        {dist > 0 && (
          <span style={{
            fontSize: 11, color: '#0a0f16', background: tc.main,
            padding: '3px 10px', borderRadius: 6, fontWeight: 600, flexShrink: 0,
          }}>{dist}K</span>
        )}
      </div>
      <div style={{ padding: '13px 16px 14px', fontSize: 15, lineHeight: 1.6, color: C.textMuted, direction: 'rtl' }}>
        {insight_text}
      </div>
    </>
  );
}

// ── Skeleton while trendData loads (same layout as TrendBlock, no visual jump) ─
function TrendBlockSkeleton({ lw, tc }) {
  if (!lw) return null;
  const label = lw.workout_type || 'אימון אחרון';
  const dist  = lw.actual_km || lw.planned_km;
  return (
    <>
      <div style={{
        background: `linear-gradient(135deg,rgba(${tc.rgb},0.28),rgba(${tc.rgb},0.05))`,
        padding: '13px 16px 11px',
        display: 'flex', alignItems: 'center', gap: 9,
        borderBottom: `1px solid rgba(${tc.rgb},0.35)`,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
          background: tc.grad,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
        }}>💡</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>תובנות המאמן</div>
          <div style={{ fontSize: 11, color: tc.light, fontWeight: 500 }}>
            {label}{lw.date ? ` · ${lw.date.slice(5,10).replace('-','/')}` : ''}
          </div>
        </div>
        {dist > 0 && (
          <span style={{
            fontSize: 11, color: '#0a0f16', background: tc.main,
            padding: '3px 10px', borderRadius: 6, fontWeight: 600, flexShrink: 0,
          }}>{dist}K</span>
        )}
      </div>
      <div style={{ padding: '13px 16px 14px' }}>
        <div style={{ height: 13, borderRadius: 6, background: `rgba(${tc.rgb},0.13)`, marginBottom: 9, width: '91%' }} />
        <div style={{ height: 13, borderRadius: 6, background: `rgba(${tc.rgb},0.09)`, marginBottom: 9, width: '76%' }} />
        <div style={{ height: 13, borderRadius: 6, background: `rgba(${tc.rgb},0.06)`, width: '52%' }} />
      </div>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AIInsightsCard({
  insights, trendData, onDismiss, onRefresh, isRefreshing,
  pendingContext, onContextSave, onContextSkip, contextSaving,
  onDeepAnalyze, deepAnalyzing,
}) {
  if (!insights && !trendData) return null;

  const hasTrend = !!trendData;
  const lw       = insights?.last_workout || null;

  // Resolve type color: unplanned card uses its own color; otherwise from last workout type
  const _tcKey = trendData?.card_mode === 'unplanned'
    ? 'unplanned'
    : (lw?.workout_type || '').toLowerCase();
  const tc = TYPE_COLORS[_tcKey] || TYPE_COLORS.easy;

  // Context flag source: pendingContext (unanswered) OR lw (already answered)
  const ctxWorkout = pendingContext || (lw?.date ? { date: lw.date } : null);

  return (
    <div style={{
      background: C.cardBg,
      ...cardBorder,
      overflow: 'hidden',
      marginBottom: 16,
      direction: 'rtl',
    }}>

      {/* ── 2.1 Main insight ── */}
      {hasTrend
        ? (trendData.card_mode === 'unplanned'
            ? <UnplannedBlock trendData={trendData} tc={tc} />
            : <TrendBlock trendData={trendData} tc={tc} />)
        : <TrendBlockSkeleton lw={lw} tc={tc} />
      }

      {/* ── 2.2 Context flag — always shown when we have a last workout ── */}
      {ctxWorkout && onContextSave && (
        <div style={{ borderTop: `1px solid ${C.divider}`, padding: '11px 16px 14px' }}>
          <ContextFlagSection
            workout={ctxWorkout}
            onSave={onContextSave}
            onSkip={onContextSkip}
            saving={contextSaving}
            initialFlag={lw?.context_flag}
            initialNote={lw?.context_note}
          />
        </div>
      )}

      {/* ── Deep AI analysis button (opt-in, once per workout) — bold orange ── */}
      {onDeepAnalyze && trendData?.today_workout?.date && (
        <div style={{ borderTop: `1px solid ${C.divider}`, padding: '12px 16px' }}>
          <button
            onClick={() => { if (!trendData.chat_analyzed && !deepAnalyzing) onDeepAnalyze(trendData.today_workout.date); }}
            disabled={trendData.chat_analyzed || deepAnalyzing}
            style={{
              width: '100%',
              background: trendData.chat_analyzed
                ? 'rgba(249,115,22,0.10)'
                : 'linear-gradient(135deg,#f97316,#ea580c)',
              border: trendData.chat_analyzed ? '1px solid rgba(249,115,22,0.35)' : 'none',
              borderRadius: 11,
              cursor: (trendData.chat_analyzed || deepAnalyzing) ? 'default' : 'pointer',
              color: trendData.chat_analyzed ? '#fb923c' : '#fff',
              fontSize: 14, fontWeight: 700,
              padding: '12px 14px',
              opacity: deepAnalyzing ? 0.7 : 1,
              boxShadow: trendData.chat_analyzed ? 'none' : '0 2px 10px rgba(249,115,22,0.35)',
              transition: 'opacity 0.15s',
            }}
          >
            {trendData.chat_analyzed
              ? '✓ האימון נותח לעומק'
              : deepAnalyzing ? '⏳ המאמן מנתח לעומק...' : '🔬 נתח את האימון לעומק עם מאמן AI'}
          </button>
        </div>
      )}

      {/* ── Refresh ── */}
      {onRefresh && (
        <div style={{ textAlign: 'left', padding: '4px 12px 8px', borderTop: `1px solid ${C.divider}` }}>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: `${C.accent}99`, fontSize: 12, padding: '2px 6px',
            }}
          >
            {isRefreshing ? '⏳' : '🔄'}
          </button>
        </div>
      )}
    </div>
  );
}
